import { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { BACKEND_URL } from "../config";
import { useTabLeader } from "./useTabLeader";

/**
 * SSE (Server-Sent Events) hook for real-time updates
 * @param {string} personnelId - Kullanıcının ID'si
 * @param {Object} handlers - Event handler'ları { eventType: handlerFunction }
 * @returns {Object} - { isConnected, reconnect }
 */
export function useSSE(personnelId, handlers = {}) {
  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const handlersRef = useRef(handlers);
  const tabIdRef = useRef(`${Date.now()}-${Math.random().toString(36).slice(2)}`);
  // Lider seçimi KULLANICI-BAZLI: aynı kullanıcının çok sekmesi tek bağlantı paylaşır (limit aşılmaz),
  // AMA aynı tarayıcıda farklı kullanıcılar (ör. müdür + BM test panelleri) AYRI bağlantı alır —
  // yoksa global lider seçiminde tek bağlantı el değiştirir ve diğer kullanıcı event kaçırır.
  const sseScopeKey = `sse:${personnelId || "anon"}`;
  const isSseLeader = useTabLeader(sseScopeKey);
  const tabBroadcastKey = useMemo(() => `pb:sse:broadcast:${personnelId || 'anon'}`, [personnelId]);
  
  // handlers değiştiğinde ref'i güncelle
  useEffect(() => {
    handlersRef.current = handlers;
  }, [handlers]);

  const dispatchToHandlers = useCallback((type, payload) => {
    if (handlersRef.current[type]) {
      handlersRef.current[type](payload);
    }
    if (handlersRef.current.onMessage) {
      handlersRef.current.onMessage(type, payload);
    }
  }, []);

  const broadcastToTabs = useCallback((type, payload) => {
    try {
      localStorage.setItem(tabBroadcastKey, JSON.stringify({
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        sourceTabId: tabIdRef.current,
        type,
        payload,
        ts: Date.now()
      }));
    } catch {
      // localStorage unavailable; no-op.
    }
  }, [tabBroadcastKey]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
      setIsConnected(false);
      console.log('[SSE] Bağlantı kapatıldı');
    }
  }, []);

  const connect = useCallback(() => {
    if (!personnelId || !isSseLeader || document.hidden || eventSourceRef.current) return;

    try {
      const url = `${BACKEND_URL}/api/sse/${personnelId}`;
      const eventSource = new EventSource(url);
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        console.log('[SSE] Bağlantı kuruldu');
        setIsConnected(true);
      };

      eventSource.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          const type = message?.type;
          const payload = message?.payload ?? message?.data ?? null;
          if (!type) return;
          dispatchToHandlers(type, payload);
          // Lider sekme aldığı event'i diğer sekmelere yayar.
          broadcastToTabs(type, payload);
        } catch (error) {
          console.error('[SSE] Mesaj parse hatası:', error);
        }
      };

      eventSource.onerror = () => {
        console.error('[SSE] Bağlantı hatası');
        setIsConnected(false);
        eventSource.close();
        eventSourceRef.current = null;
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }

        // 5 saniye sonra yeniden bağlan
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log('[SSE] Yeniden bağlanılıyor...');
          if (!eventSourceRef.current && personnelId && isSseLeader && !document.hidden) {
            const newUrl = `${BACKEND_URL}/api/sse/${personnelId}`;
            const newEventSource = new EventSource(newUrl);
            eventSourceRef.current = newEventSource;
            
            newEventSource.onopen = () => {
              console.log('[SSE] Bağlantı yeniden kuruldu');
              setIsConnected(true);
            };
            newEventSource.onmessage = eventSource.onmessage;
            newEventSource.onerror = eventSource.onerror;
          }
        }, 5000);
      };

    } catch (error) {
      console.error('[SSE] Bağlantı kurulamadı:', error);
    }
  }, [personnelId, isSseLeader, dispatchToHandlers, broadcastToTabs]);

  // Lider tab dışındaki sekmeler için localStorage tabanlı event yayını
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key !== tabBroadcastKey || !event.newValue) return;
      try {
        const parsed = JSON.parse(event.newValue);
        if (!parsed || parsed.sourceTabId === tabIdRef.current) return;
        if (!parsed.type) return;
        dispatchToHandlers(parsed.type, parsed.payload ?? null);
      } catch (error) {
        console.error('[SSE] Tab broadcast parse hatası:', error);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [dispatchToHandlers, tabBroadcastKey]);

  useEffect(() => {
    if (!isSseLeader || document.hidden) {
      disconnect();
      return;
    }
    connect();
    return () => disconnect();
  }, [connect, disconnect, isSseLeader]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) {
        disconnect();
      } else if (isSseLeader) {
        connect();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [connect, disconnect, isSseLeader]);

  const reconnect = useCallback(() => {
    disconnect();
    setTimeout(() => connect(), 100);
  }, [disconnect, connect]);

  return {
    isConnected,
    reconnect
  };
}

/**
 * Mesai yönetimi için özelleştirilmiş SSE hook
 * @param {string} personnelId 
 * @param {Object} callbacks - { onSurveyUpdate, onDraftPlanUpdate, onNotification }
 */
export function useMesaiSSE(personnelId, callbacks = {}) {
  const handlers = {
    SURVEY_UPDATE: (payload) => {
      console.log('[SSE] Anket güncellendi:', payload);
      callbacks.onSurveyUpdate?.(payload);
    },
    DRAFT_PLAN_UPDATE: (payload) => {
      console.log('[SSE] Taslak plan güncellendi:', payload);
      callbacks.onDraftPlanUpdate?.(payload);
    },
    OFFICIAL_HOLIDAY_UPDATE: (payload) => {
      console.log('[SSE] Resmi/idari tatil güncellendi:', payload);
      callbacks.onHolidayUpdate?.(payload);
    },
    NOTIFICATION: (payload) => {
      console.log('[SSE] Bildirim alındı:', payload);
      callbacks.onNotification?.(payload);
    },
    survey_created: (payload) => {
      console.log('[SSE] Yeni anket oluşturuldu:', payload);
      callbacks.onSurveyUpdate?.({ action: 'created', ...payload });
    },
    onMessage: (type, payload) => {
      // Genel mesaj handler - debugging için
      console.log(`[SSE] Event: ${type}`, payload);
    }
  };

  return useSSE(personnelId, handlers);
}

export default useSSE;
